import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FolderOpen, 
  CheckSquare, 
  Settings as SettingsIcon,
  Bot,
  Clock,
  Menu,
  X,
  Home,
  Plus,
  Search,
  Bell,
  ChevronDown,
  User,
  Building,
  Plus as PlusIcon,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock as ClockIcon,
  LogOut,
  Shield,
  Users as UsersIcon,
  BarChart3,
  HelpCircle
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import GlobalAIDrawer from './GlobalAIDrawer';

interface LayoutProps {
  children: React.ReactNode;
}

interface Workspace {
  id: string;
  name: string;
  type: 'personal' | 'organization';
  color: string;
  isActive: boolean;
}

interface Notification {
  id: string;
  type: 'task' | 'project' | 'reminder' | 'event';
  title: string;
  message: string;
  time: string;
  isRead: boolean;
}

interface UserAccount {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar: string;
  role: 'admin' | 'user';
  isActive: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [showGlobalAI, setShowGlobalAI] = useState(false);
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  const isDark = document.documentElement.classList.contains('dark');

  // Mock user data
  const [currentUser] = useState<UserAccount>({
    id: '1',
    firstName: 'Favi',
    lastName: 'Ours',
    email: 'fvourszn@gmail.com',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    role: 'admin',
    isActive: true
  });

  // Mock saved accounts (like Gmail)
  const [savedAccounts] = useState<UserAccount[]>([
    {
      id: '1',
      firstName: 'Favi',
      lastName: 'Ours',
      email: 'fvourszn@gmail.com',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      role: 'admin',
      isActive: true
    },
    {
      id: '2',
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah@acmecorp.com',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      role: 'user',
      isActive: false
    },
    {
      id: '3',
      firstName: 'Mike',
      lastName: 'Chen',
      email: 'mike@freelance.com',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      role: 'user',
      isActive: false
    }
  ]);

  // Mock workspaces
  const [workspaces, setWorkspaces] = useState<Workspace[]>([
    { id: '1', name: 'Personal', type: 'personal', color: '#8B5CF6', isActive: true },
    { id: '2', name: 'Acme Corp', type: 'organization', color: '#10B981', isActive: false },
    { id: '3', name: 'Freelance', type: 'personal', color: '#F59E0B', isActive: false }
  ]);

  // Mock notifications
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'task',
      title: 'Task Due Soon',
      message: 'Review project proposal due in 2 hours',
      time: '2h',
      isRead: false
    },
    {
      id: '2',
      type: 'project',
      title: 'Project Update',
      message: 'Sage development milestone completed',
      time: '1d',
      isRead: false
    },
    {
      id: '3',
      type: 'reminder',
      title: 'Daily Standup',
      message: 'Team meeting in 30 minutes',
      time: '30m',
      isRead: true
    }
  ]);

  // Update clock every second
  useEffect(() => {
    const open = () => setShowGlobalAI(true);
    window.addEventListener('open-global-ai', open);
    return () => window.removeEventListener('open-global-ai', open);
  }, []);

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.workspace-menu') && !target.closest('.notification-menu') && !target.closest('.profile-menu')) {
        setShowWorkspaceMenu(false);
        setShowNotifications(false);
        setShowProfileMenu(false);
        setShowAccountSwitcher(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // apply saved theme class (e.g., theme-red)
    const themeClass = localStorage.getItem('theme-class');
    const root = document.documentElement;
    root.classList.remove('theme-red');
    if (themeClass) root.classList.add(themeClass);
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Projects', href: '/projects', icon: FolderOpen },
    { name: 'Tasks', href: '/tasks', icon: CheckSquare },
    { name: 'AI Assistant', href: '#open-ai', icon: Bot },
    { name: 'Time Map', href: '/timemap', icon: Clock },
    { name: 'Settings', href: '/settings', icon: SettingsIcon },
  ];

  const adminNavigation = [
    { name: 'Users', href: '/users', icon: UsersIcon },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  ];

  const handleWorkspaceSwitch = (workspaceId: string) => {
    setWorkspaces(prev => prev.map(w => ({
      ...w,
      isActive: w.id === workspaceId
    })));
    setShowWorkspaceMenu(false);
  };

  const handleCreateWorkspace = () => {
    const newWorkspace: Workspace = {
      id: Date.now().toString(),
      name: 'New Workspace',
      type: 'personal',
      color: '#3B82F6',
      isActive: false
    };
    setWorkspaces(prev => [...prev, newWorkspace]);
    setShowWorkspaceMenu(false);
  };

  const handleAccountSwitch = (accountId: string) => {
    // In real app, this would switch the actual user session
    console.log('Switching to account:', accountId);
    setShowAccountSwitcher(false);
    setShowProfileMenu(false);
  };

  const handleLogout = () => {
    // In real app, this would clear the session
    navigate('/auth/signin');
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'task': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'project': return <Building className="w-4 h-4 text-purple-500" />;
      case 'reminder': return <ClockIcon className="w-4 h-4 text-orange-500" />;
      case 'event': return <Calendar className="w-4 h-4 text-green-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const activeWorkspace = workspaces.find(w => w.isActive);
  const unreadNotifications = notifications.filter(n => !n.isRead).length;

  return (
    <div className={`flex h-screen bg-gradient-to-br ${colors.background} overflow-hidden`}>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Sticky with own scroll */}
      <aside className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-80 lg:w-64 
        transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        transition-transform duration-300 ease-in-out
        ${document.documentElement.classList.contains('dark') ? 'bg-gray-900 border-gray-700' : 'bg-white border-slate-200'} backdrop-blur-md border-r shadow-soft
        flex flex-col h-screen
      `}>
        {/* Header with User Avatar */}
        <div className={`h-16 px-4 flex items-center justify-between border-b ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
          <div className="flex items-center space-x-3">
            {/* User Avatar */}
            <div className="relative profile-menu">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-200"
              >
                <img 
                  src={currentUser.avatar} 
                  alt={currentUser.firstName}
                  className="w-full h-full object-cover"
                />
              </button>

              {/* Profile Dropdown */}
              {showProfileMenu && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50">
                  {/* User Info */}
                  <div className="p-4 border-b border-gray-700">
                    <div className="flex items-center space-x-3">
                      <img 
                        src={currentUser.avatar} 
                        alt={currentUser.firstName}
                        className="w-12 h-12 rounded-full"
                      />
                      <div>
                        <div className="text-white font-semibold">{currentUser.firstName} {currentUser.lastName}</div>
                        <div className="text-gray-400 text-sm">{currentUser.email}</div>
                        <div className="flex items-center space-x-1 mt-1">
                          <Shield className="w-3 h-3 text-purple-500" />
                          <span className="text-xs text-purple-500 capitalize">{currentUser.role}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="p-2">
                    <button
                      onClick={() => navigate('/profile')}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-white/5 hover:text-white transition-all duration-200"
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </button>
                    <button
                      onClick={() => navigate('/settings')}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-white/5 hover:text-white transition-all duration-200"
                    >
                      <SettingsIcon className="w-4 h-4" />
                      <span>Settings</span>
                    </button>
                    <button
                      onClick={() => setShowAccountSwitcher(!showAccountSwitcher)}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-white/5 hover:text-white transition-all duration-200"
                    >
                      <UsersIcon className="w-4 h-4" />
                      <span>Switch Account</span>
                    </button>
                    <button
                      onClick={() => navigate('/help')}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-white/5 hover:text-white transition-all duration-200"
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span>Help & Support</span>
                    </button>
                  </div>

                  {/* Logout */}
                  <div className="p-2 border-t border-gray-700">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Account Switcher */}
              {showAccountSwitcher && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50">
                  <div className="p-4 border-b border-gray-700">
                    <h3 className="text-white font-semibold mb-3">Switch Account</h3>
                    <div className="space-y-2">
                      {savedAccounts.map((account) => (
                        <button
                          key={account.id}
                          onClick={() => handleAccountSwitch(account.id)}
                          className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
                            account.isActive 
                              ? 'bg-white/10 text-white' 
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <img 
                            src={account.avatar} 
                            alt={account.firstName}
                            className="w-10 h-10 rounded-full"
                          />
                          <div className="flex-1 text-left">
                            <div className="font-medium">{account.firstName} {account.lastName}</div>
                            <div className="text-sm text-gray-500">{account.email}</div>
                          </div>
                          {account.isActive && (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => navigate('/auth/signin')}
                      className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-300 hover:bg-white/5 hover:text-white transition-all duration-200"
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>Add Another Account</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

                  {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto stealth-scroll">
            <div className="px-3 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href === '#open-ai' ? location.pathname : item.href}
                    onClick={(e) => {
                      if (item.href === '#open-ai') {
                        e.preventDefault();
                        setShowGlobalAI(true);
                      }
                      setSidebarOpen(false);
                    }}
                    className={(() => {
                      const base = 'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200';
                      const active = isDark ? 'bg-white/10 text-white border border-white/20' : 'bg-slate-100 text-slate-900 border border-slate-200';
                      const inactive = isDark ? 'text-gray-300 hover:bg-white/5 hover:text-white' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900';
                      return `${base} ${isActive ? active : inactive}`;
                    })()}
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}

              {/* Admin Section */}
              {currentUser.role === 'admin' && (
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="px-3 mb-3">
                    <h3 className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Admin
                    </h3>
                  </div>
                  {adminNavigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={(() => {
                          const base = 'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200';
                          const active = isDark ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-purple-50 text-purple-700 border border-purple-200';
                          const inactive = isDark ? 'text-gray-300 hover:bg-purple-500/10 hover:text-purple-300' : 'text-slate-700 hover:bg-purple-50 hover:text-purple-700';
                          return `${base} ${isActive ? active : inactive}`;
                        })()}
                      >
                        <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                        <span>{item.name}</span>
                </Link>
              );
            })}
                </div>
              )}
            </div>

          {/* Clock Widget */}
          <div className="px-3 mt-6">
            <div className={`pt-4 ${isDark ? 'border-t border-gray-700' : 'border-t border-slate-200'}`}>
              <div className={`${isDark ? 'bg-white/5' : 'bg-slate-50'} rounded-xl p-3 text-center`}>
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {currentTime.toLocaleTimeString('en-US', { 
                    hour12: false, 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
                <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  {currentTime.toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="px-3 mt-4">
            <div className={`pt-4 ${isDark ? 'border-t border-gray-700' : 'border-t border-slate-200'}`}>
              <h3 className={`text-xs font-medium uppercase tracking-wider mb-3 px-3 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Quick Actions
              </h3>
              <div className="space-y-1">
                <button className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isDark ? 'text-gray-300 hover:bg-white/5 hover:text-white' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'}`}>
                  <Plus className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>New Project</span>
                </button>
                <button className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isDark ? 'text-gray-300 hover:bg-white/5 hover:text-white' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'}`}>
                  <Plus className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>New Task</span>
                </button>
              </div>
            </div>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className={`h-16 px-4 flex items-center justify-between ${colors.surface} backdrop-blur border-b ${colors.border} lg:hidden`}>
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center space-x-2">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${colors.primary} shadow-glow flex items-center justify-center`}>
              <span className="text-white font-bold text-sm">S</span>
            </div>
            <h1 className={`text-lg font-semibold bg-gradient-to-r ${colors.primary} bg-clip-text text-transparent`}>Sage</h1>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => setShowGlobalAI(true)}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white"
              title="Open Sage"
            >
              <Bot className="w-5 h-5" />
            </button>
            <div className="w-2" />
          </div>
        </header>

        {/* Desktop Header */}
        <header className={`h-16 px-6 flex items-center justify-between ${colors.surface} backdrop-blur border-b ${colors.border} hidden lg:flex`}>
          {/* Left: Workspace Switcher */}
          <div className="flex items-center space-x-4">
            {/* Workspace Switcher */}
            <div className="relative workspace-menu">
              <button
                onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-all duration-200"
              >
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: activeWorkspace?.color }}
                  />
                  <span className="text-white font-medium">{currentUser.firstName}</span>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {/* Workspace Dropdown */}
              {showWorkspaceMenu && (
                <div className={`absolute top-full left-0 mt-2 w-64 rounded-xl shadow-2xl border z-50 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
                  <div className="p-3 border-b border-gray-700">
                    <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Workspaces</div>
                    {workspaces.map((workspace) => (
                      <button
                        key={workspace.id}
                        onClick={() => handleWorkspaceSwitch(workspace.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-all duration-200 ${isDark ? (
                          workspace.isActive ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        ) : (
                          workspace.isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                        )}`}
                      >
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: workspace.color }}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{workspace.name}</div>
                          <div className={`text-xs capitalize ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{workspace.type}</div>
                        </div>
                        {workspace.isActive && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="p-2">
                    <button
                      onClick={handleCreateWorkspace}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${isDark ? 'text-gray-300 hover:bg-white/5 hover:text-white' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'}`}
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>Create Workspace</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Smart Search & Notification Hub */}
          <div className="flex items-center space-x-4">
            {/* Smart Search Bar */}
            <div className="relative">
              <div className={`flex items-center transition-all duration-300 ${
                searchExpanded ? 'w-80' : 'w-12'
              }`}>
                <div className="relative flex items-center">
                  <button
                    onClick={() => setSearchExpanded(!searchExpanded)}
                    className={`p-2 rounded-lg transition-all duration-300 ${
                      searchExpanded 
                        ? 'bg-white/10 text-white' 
                        : 'hover:bg-white/5 text-gray-400 hover:text-white'
                    }`}
                  >
                    <Search className="w-5 h-5" />
                  </button>
                  {searchExpanded && (
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search projects, tasks, or ask AI..."
                      className="ml-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 w-64"
                      autoFocus
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Notification Hub */}
            <div className="relative notification-menu">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-all duration-200"
              >
                <Bell className="w-5 h-5" />
                {unreadNotifications > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-xs text-white font-bold">{unreadNotifications}</span>
                  </div>
                )}
              </button>

              {/* Notifications Panel */}
              {showNotifications && (
                <div className={`absolute top-full right-0 mt-2 w-80 rounded-xl shadow-2xl border z-50 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
                  <div className={`p-4 border-b ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between">
                      <h3 className={`${isDark ? 'text-white' : 'text-slate-900'} font-semibold`}>Notifications</h3>
                      <button className={`text-xs ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                        Mark all read
                      </button>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto stealth-scroll">
                    {notifications.length === 0 ? (
                      <div className={`p-4 text-center ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No notifications</p>
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 transition-all duration-200 ${isDark ? 'border-b border-gray-700 hover:bg-white/5' : 'border-b border-slate-200 hover:bg-slate-50'} ${
                            !notification.isRead ? (isDark ? 'bg-white/5' : 'bg-slate-50') : ''
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            {getNotificationIcon(notification.type)}
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className={`${isDark ? 'text-white' : 'text-slate-900'} font-medium text-sm`}>{notification.title}</h4>
                                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{notification.time}</span>
                              </div>
                              <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'} text-sm mt-1`}>{notification.message}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* LED Display Area */}
            <div className="hidden xl:block">
              <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/10">
                <div className="text-xs text-gray-400 mb-1">Next Up</div>
                <div className="text-sm text-white font-medium">Team Standup • 30m</div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
      <GlobalAIDrawer isOpen={showGlobalAI} onClose={() => setShowGlobalAI(false)} />
      {/* Docked Sage ribbon - bottom right */}
      {!showGlobalAI && (
        <button
          onClick={() => setShowGlobalAI(true)}
          className={`fixed right-4 bottom-6 z-40 px-4 py-2 rounded-full shadow-lg text-white hover:opacity-95 bg-gradient-to-r ${colors.primary} flex items-center space-x-2`}
          title="Chat with Sage"
        >
          <Bot className="w-4 h-4" />
          <span>Sage</span>
        </button>
      )}
    </div>
  );
};

export default Layout; 